"use client";

import React from "react";
import { Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  value: "25+",
  label: "Years DC / Cloud / PE"
}, {
  value: "$7.6B+",
  label: "M&A Integrations"
}, {
  value: "120+",
  label: "DCs Operated"
}, {
  value: "$400M+",
  label: "VC AUM"
}, {
  value: "5",
  label: "Patents Filed"
}, {
  value: "52+",
  label: "Portfolio Cos."
}];
const valueAdds = [{
  title: "Operational Underwriting — No Black Box",
  desc: "Led 120+ global data centers at Equinix with $1B+ in annual revenue under management. N2N’s underwriting is grounded in first-hand cost structures, SLA benchmarks, and power capacity planning."
}, {
  title: "Proven M&A Integration at Scale",
  desc: "Directed $7.6B+ in DC M&A across 60+ assets — diligence, valuation, synergy capture, and day-1 operational readiness."
}, {
  title: "AI Architecture Depth",
  desc: "Technical fluency in GPU-optimized compute density, hyperscaler roadmap alignment, and AI/ML infrastructure stacks enables precise underwriting of power, thermal design, and tenant lease structures."
}, {
  title: "Live LP Network\xA0",
  desc: "25+ years across institutional PE, venture capital, and data center operations has built deep relationships with family offices, sovereign wealth funds, and infrastructure lenders across the U.S., MENA, and APAC, providing N2N with direct access to institutional LP channels from day one."
}, {
  title: "Patented Proprietary Advantage",
  desc: "Founder credentials include 5 patents in data center high availability, SDN transformation, and co-location performance scoring, reflecting deep technical expertise that underpins N2N's infrastructure underwriting and site evaluation approach."
}, {
  title: "Cross-Border Capital Formation",
  desc: "Extensive cross-border transaction experience spanning the U.S., Middle East, and Asia, including co-GP and JV structuring across complex multi-jurisdictional regulatory environments, directly informs N2N's India and Southeast Asia capital formation and joint venture frameworks."
}];
export default function NawazProfileSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section ref={ref} className="relative overflow-hidden bg-[#FDFAF3] py-24 text-[#1C1917] md:py-32">
      <div className="pointer-events-none absolute left-0 top-0 h-[560px] w-[560px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-[#E7D8B8]/55 blur-[100px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:px-10 lg:grid-cols-[340px_1fr] lg:gap-16">
        <aside className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="group relative overflow-hidden rounded-xl border border-[#D8C29A] bg-[#F5EDD8] shadow-sm">
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://decent-kid.10web.cloud/wp-content/uploads/2026/04/IMG_0458-scaled.jpeg" alt="Syed Natif Nawaz" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-t from-black/90 via-black/55 to-transparent p-6 pt-24 opacity-0 translate-y-full transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
              <Button asChild className="w-full rounded-sm bg-[#B45309] text-white hover:bg-[#B45309]/90 font-mono tracking-wider">
                <Link to="https://www.linkedin.com/in/syed-natif-nawaz/" newTab>
                  <Linkedin className="mr-2 h-4 w-4" />
                  Direct Contact
                </Link>
              </Button>
            </div>

            <div className="h-1 bg-[#B45309]" />
          </div>

          <div className="mt-8">
            <h2 className="font-serif text-3xl font-bold leading-tight text-[#1C1917]">
              Syed Natif Nawaz
            </h2>
            <p className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#B45309]">
              Co-Founder & General Partner
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 overflow-hidden rounded-lg border border-[#D8C29A] bg-[#FDFAF3] shadow-sm">
            {stats.map(stat => <div key={stat.label} className="border-b border-r border-[#D8C29A] p-4 text-center [&:nth-child(3n)]:border-r-0 [&:nth-last-child(-n+3)]:border-b-0">
                <div className="font-serif text-lg font-semibold text-[#1C1917]">
                  {stat.value}
                </div>
                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-[#1C1917]/55">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </aside>

        <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
        transitionDelay: "150ms"
      }}>
          <p className="max-w-4xl text-lg leading-relaxed text-[#1C1917]/70">
            25+ years at the intersection of global data center operations, AI
            infrastructure, and institutional private equity — from Cisco
            engineer to Equinix Senior Director managing $1B+ revenue
            portfolios, to GP Co-Founder. Nawaz brings the rare combination of
            hands-on DC operational depth, $7.6B+ M&A execution experience, and
            a live institutional LP network to N2N Capital.
          </p>

          <div className="my-10 h-px w-full bg-[#D8C29A]" />

          <div className="mb-8">
            <span className="font-mono text-xs font-bold uppercase tracking-[0.32em] text-[#B45309]">
              Value-Add to N2N Capital
            </span>
          </div>

          <div className="grid gap-7 md:grid-cols-2">
            {valueAdds.map((item, i) => <div key={item.title} className={`rounded-xl border border-[#D8C29A] bg-white/60 p-7 shadow-sm transition-all duration-700 hover:-translate-y-1 hover:border-[#B45309]/40 hover:shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${250 + i * 100}ms`
          }}>
                <h3 className="font-serif text-xl font-bold leading-snug text-[#1C1917]">
                  {item.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#1C1917]/65">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10">
        <p className="mt-16 font-mono text-[10px] leading-relaxed tracking-wider text-[#1C1917]/40 uppercase whitespace-nowrap">
          * Platform metrics reflect contributions during senior leadership tenure and represent the scale of operations supported — not assets under Management.
        </p>
      </div>
    </section>;
}